import { BusinessError, EntityKind, ErrorKind } from '../errors';
import { TiersPersonnaliteJuridique, TiersSchema, TiersTypeFamille, TiersValidStatuses, isResultSuccess, isResultError, } from '../types';
/**
 * Tiers service
 */
class TiersService {
    tiersAdapter;
    i18nService;
    dateFormatterService;
    /**
     * Tiers Service.
     */
    constructor({ tiersAdapter, i18nService, dateFormatterService }) {
        this.tiersAdapter = tiersAdapter;
        this.i18nService = i18nService;
        this.dateFormatterService = dateFormatterService;
    }
    async getTiersReferent(tiersId) {
        const publicSettings = await this.tiersAdapter.getTiersPublicSettings();
        if (isResultSuccess(publicSettings) && publicSettings.response?.displayTiersReferentUsager.active) {
            return this.tiersAdapter.getTiersReferent(tiersId);
        }
        return {
            error: new BusinessError(ErrorKind.NOT_FOUND, {
                entity: EntityKind.TIERS,
            }),
        };
    }
    /**
     * Build tiers physique
     * * this function mutates tiersInfo parameter
     *
     */
    async buildTiersPhysique(tiers, personnaliteJuridique, famille, tiersInfo) {
        if (personnaliteJuridique !== TiersPersonnaliteJuridique.PHYSIQUE)
            return;
        tiersInfo.tiersName = '';
        if (tiers.individu?.Naissance?.DateNaissance) {
            const dateNaissance = this.dateFormatterService.asDate(tiers.individu.Naissance.DateNaissance, { style: 'long' });
            // build birth value (dateNaissance [Localite])
            tiersInfo.birth = `${this.i18nService.translate('tiers-menu.infos.birth.date')} ${dateNaissance}`;
            if (tiers.individu.Naissance.LieuNaissance?.Localite) {
                tiersInfo.birth += ` ${this.i18nService.translate('tiers-menu.infos.birth.place')} ${tiers.individu.Naissance.LieuNaissance.Localite.value}`;
            }
        }
        // build birthName
        if (tiers.individu?.Noms.NomFamille)
            tiersInfo.birthName = `${this.i18nService.translate('tiers-menu.infos.birth.name')} : ${tiers.individu.Noms.NomFamille}`;
        if (famille === TiersTypeFamille.PARTICULIER) {
            tiersInfo.title = this.i18nService.translate('tiers-menu.infos.title');
        }
    }
    /**
     * Extract info from current tiers.
     *
     * @returns the tiers user info object
     */
    async getCurrentTiersInfos(currentTiers, currentFamilleCount) {
        let tiersRes;
        if (!currentTiers) {
            tiersRes = await this.tiersAdapter.getCurrentTiers();
        }
        let tiers;
        try {
            // check adapter response
            if (tiersRes?.error) {
                throw tiersRes.error;
            }
            // parse response with zod
            tiers = TiersSchema.parse(currentTiers ?? tiersRes.response);
            // build tiers info result
            const tiersInfo = {
                birth: '',
                birthName: '',
                tiersName: tiers.title,
                title: tiers.title,
            };
            let emails = [];
            const id = tiers.id;
            const reference = tiers.reference;
            const enseigne = tiers.enseigne;
            const formeJuridique = tiers.formeJuridique?.title;
            let siret = '';
            const type = tiers.famille.title;
            const famille = tiers.famille.expand?.typeFamille;
            const personnaliteJuridique = tiers.famille.expand?.personnaliteJuridique;
            const adresse = {
                pointRemise: '',
                complement: '',
                rue: '',
                mentionDistribution: '',
                commune: '',
                pays: '',
            };
            const dateUpdate = this.dateFormatterService.asDate(tiers.date, { style: 'long' });
            // Tiers PHYISIQUE
            await this.buildTiersPhysique(tiers, personnaliteJuridique, famille, tiersInfo);
            // build emails
            if (tiers.courriels && tiers.courriels.length > 0) {
                emails = tiers.courriels.map(({ value }) => value);
            }
            // build siret
            if (tiers.SIRET?.SIREN) {
                siret = `${this.i18nService.translate('tiers-menu.infos.siret')} ${tiers.SIRET.SIREN} ${tiers.SIRET.NIC}`;
            }
            // build adresse
            if (tiers.situations[0]?.geographique) {
                const geographique = tiers.situations[0].geographique;
                adresse.pointRemise = geographique.PointRemise;
                adresse.complement = geographique.Complement;
                adresse.rue = geographique.NomVoie;
                adresse.mentionDistribution = geographique.MentionDistribution;
                if (geographique.CodePostal)
                    adresse.commune = `${geographique.CodePostal} `;
                if (geographique.Localite)
                    adresse.commune += geographique.Localite.value;
                if (geographique.Pays)
                    adresse.pays = geographique.Pays.value;
            }
            let referent = null;
            // add referent
            if (TiersValidStatuses.includes(tiers.status)) {
                // Get referent only if tiers is valid
                const referentRes = await this.getTiersReferent(id);
                if (isResultSuccess(referentRes)) {
                    referent = referentRes.response;
                }
            }
            let famillesTiersCount = currentFamilleCount;
            if (!famillesTiersCount) {
                const result = await this.tiersAdapter.getFamillesTiersCount();
                if (isResultSuccess(result)) {
                    famillesTiersCount = result.response;
                }
            }
            let hasOnlyTiersParticulier = true;
            for (const famille of Object.keys(famillesTiersCount ?? {})) {
                if (famille !== TiersTypeFamille.PARTICULIER.toString()) {
                    hasOnlyTiersParticulier = false;
                    break;
                }
            }
            return {
                response: {
                    showChangeTiersButton: !hasOnlyTiersParticulier,
                    id,
                    reference,
                    ...tiersInfo,
                    enseigne,
                    formeJuridique,
                    type,
                    famille,
                    personnaliteJuridique,
                    siret,
                    adresse,
                    emails,
                    dateUpdate,
                    referent,
                },
            };
        }
        catch (error) {
            if (error instanceof BusinessError && error.type === ErrorKind.BUSINESS) {
                // No tiers selected
                const userTiers = await this.tiersAdapter.getAllCurrentUserTiers();
                if (isResultSuccess(userTiers)) {
                    return {
                        response: {
                            showChangeTiersButton: !!userTiers.response.total,
                            title: userTiers.response.total ? this.i18nService.translate('tiers-menu.infos.no-tiers-selected') : '',
                        },
                    };
                }
            }
            console.error(error);
            if (error instanceof BusinessError) {
                return { error };
            }
            else {
                throw error;
            }
        }
    }
    /**
     * Return true if current tiers can manage demandes apprenants.
     */
    async canCurrentTiersManageApprenants() {
        try {
            const tiersRes = await this.tiersAdapter.getCurrentTiers();
            // check adapter response
            if (isResultError(tiersRes)) {
                throw tiersRes.error;
            }
            // parse response with zod
            const tiers = TiersSchema.parse(tiersRes.response);
            return tiers.gestionDemandesApprenants ?? false;
        }
        catch {
            return false;
        }
    }
    isOrganismeDeFormation(tiers) {
        return tiers?.features?.organismeFormation === true;
    }
}
export { TiersService };
//# sourceMappingURL=tiers-service.js.map